import config from "react-global-configuration";
import { checkDate } from "common/helpers/Date";
import { toOneLiner } from "common/records/Address";
import { JobRecord } from "common/records/Job";
import { IJobBasicData, JobBasicRecord, jobBasicFromJson } from "common/records/JobBasic";
import { Record } from "immutable";
import { addAuthUser } from "common/components/CustomLink";
import { EventMetadataRecord, IEventMetadata, eventMetadataFromJSON } from "common/records/EventMetadata";
import { DateStringNumber } from "common/records";

export const eventFromJSON = (json: Partial<IEventData>): EventRecord => {
  const recordData: IEventRecord = { ...(json as any) };

  if (json.job) {
    recordData.job = jobBasicFromJson(json.job);
  }

  if (json.start_time) {
    recordData.start_time = checkDate(json.start_time);
  }

  if (json.end_time) {
    recordData.end_time = checkDate(json.end_time);
  }

  if (json.metadata) {
    recordData.metadata = eventMetadataFromJSON(json.metadata);
  }

  // Why: Cronofy-synced events (any event with `metadata.cronofy_id`) must be
  // read-only everywhere they surface — FullCalendar drag/resize, modal Delete,
  // anywhere a consumer reads `event.editable`. Enforce at hydration so every
  // reader gets the right answer without an extra `metadata.cronofy_id` check.
  if (json.metadata?.cronofy_id) {
    recordData.editable = false;
  }

  return new EventRecord(recordData);
};

export const initDescription = (job: JobRecord, accessUid: string): string => {
  const location = `Location:\n${toOneLiner(job.address)}\n\n`;
  const additionalInfo = job.additional_info ? `Additional Info:\n${job.additional_info}\n\n` : "";
  const phoneNumbers = `Phone Numbers:\n${job.phone_numbers.map((pn) => `${pn.name}: ${pn.number}`).join("\n")}\n\n`;
  const link = addAuthUser(`${config.get("APP_URL")}/jobs/${job.id}`, accessUid);
  return `${location}${additionalInfo}${phoneNumbers}Link: ${link}`;
};

export interface IEventData {
  id: number;
  summary: string;
  start_time: DateStringNumber;
  end_time: DateStringNumber;
  job_id: number;
  job?: IJobBasicData;
  description: string;
  metadata?: IEventMetadata;
  events_result_id?: number;
  events_result_reason_id?: number;
  notes?: string;
  user_id?: number;
  org_id: number;
  editable?: boolean;
}

export interface IEventRecord {
  id: number;
  summary: string;
  start_time: Date;
  end_time: Date;
  job_id: number;
  job: JobBasicRecord;
  description: string;
  metadata?: EventMetadataRecord;
  events_result_id: number;
  events_result_reason_id: number;
  notes: string;
  user_id: number;
  org_id: number;
  editable: boolean;

  loading: boolean;
}

export const defaultEventProps = {
  id: 0,
  summary: "",
  start_time: new Date(),
  end_time: new Date(),
  job_id: 0,
  job: jobBasicFromJson({}),
  description: "",
  metadata: eventMetadataFromJSON({}),
  events_result_id: 0,
  events_result_reason_id: 0,
  notes: "",
  user_id: 0,
  org_id: 0,
  editable: true,
  loading: false,
};

export class EventRecord extends Record(defaultEventProps) implements IEventRecord {}
